'use client';
import { DocumentCheckIcon, XMarkIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RecentHighRiskIssues from '~/app/(secured)/[workspaceId]/content-scanner/_components/Dashboard/RecentHighRiskIssues';
import { SkeletonActionButton, type ActionOption } from '~/components/ActionButton';
import Button from '~/components/Button';
import { DrawerBody, DrawerHeader, DrawerShell } from '~/components/Drawer';
import Overlay from '~/components/Overlay';
import { Routes } from '~/config';
import { api } from '~/utils/api';
import { serverError } from '~/utils/serverError';
import ScannedPages from './ScannedPages';
import { useRouter } from 'next/navigation';
import { useModal } from '~/components/Modal';
import ConfirmationModal from '~/components/ConfirmationModal';
import Skeleton from '~/components/Loading/Skeleton';
import { toast } from '~/utils/toast';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import useInvalidateScannerAggregates from '~/hooks/useInvalidateScannerAggregates';

type ReportProps = {
  reportId: string;
  brandId: string;
  onClose: () => void;
  onPendingDelete?: (reportId: string) => void;
  onUndoDelete?: (reportId: string) => void;
  onDeleteSettled?: (reportId: string) => void;
};

export default function Report({
  onClose,
  brandId,
  reportId,
  onPendingDelete,
  onUndoDelete,
  onDeleteSettled,
}: ReportProps) {
  const router = useRouter();
  const { workspaceId } = useWorkspace();
  const { data: report, isLoading: isReportLoading } = api.scanner.getScanReport.useQuery({
    reportId,
  });
  const { mutateAsync: updateScanReport } = api.scanner.updateScanReport.useMutation();
  const { mutateAsync: deleteScanReport } = api.scanner.deleteScanReport.useMutation();
  const utils = api.useUtils();
  const invalidateScannerAggregates = useInvalidateScannerAggregates();
  const [isMarkingReviewed, setIsMarkingReviewed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isMountedRef = useRef(true);
  const { modalRef, openModal, closeModal } = useModal();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isReviewed = !!report?.reviewedByUserId;
  const allDocumentsReviewed = !!report?.allDocumentsReviewed;

  const handleMarkAsReviewedChange = useCallback(
    async (e: React.MouseEvent, markedAsReviewed: boolean) => {
      e.stopPropagation();
      try {
        setIsMarkingReviewed(true);
        await updateScanReport({ id: reportId, isReviewed: markedAsReviewed });
        await utils.scanner.getScanReport.invalidate({ reportId });
        utils.scanner.getScanHistory.invalidate();
        await invalidateScannerAggregates();
      } catch (error: any) {
        serverError(error as Error);
      } finally {
        setIsMarkingReviewed(false);
      }
    },
    [reportId, updateScanReport, utils.scanner, invalidateScannerAggregates],
  );

  const handleDelete = useCallback(() => {
    closeModal();
    onPendingDelete?.(reportId);

    toast.undoable({
      message: 'Report deleted',
      onUndo: () => {
        onUndoDelete?.(reportId);
      },
      onCommit: async () => {
        let shouldClose = false;
        try {
          if (isMountedRef.current) {
            setIsDeleting(true);
          }
          await deleteScanReport({ id: reportId });
          onDeleteSettled?.(reportId);
          await utils.scanner.getScanHistory.invalidate();
          await invalidateScannerAggregates();
          shouldClose = true;
        } catch (e) {
          const message = (e as Error).message ?? '';
          if (/not found/i.test(message)) {
            onDeleteSettled?.(reportId);
            await utils.scanner.getScanHistory.invalidate();
            await invalidateScannerAggregates();
            shouldClose = true;
          } else {
            serverError(e as Error);
            onUndoDelete?.(reportId);
          }
        } finally {
          if (isMountedRef.current) {
            setIsDeleting(false);
          }
          if (shouldClose) {
            onClose();
          }
        }
      },
    });
  }, [
    reportId,
    deleteScanReport,
    utils.scanner,
    invalidateScannerAggregates,
    closeModal,
    onClose,
    onPendingDelete,
    onUndoDelete,
    onDeleteSettled,
  ]);

  // Define options for SkeletonActionButton using ActionOption type
  const actionOptions = useMemo<ActionOption[]>(
    () => [
      ...(report?.brand?.name
        ? [
            {
              icon: EyeIcon,
              label: 'View jobs',
              onClick: (e) => {
                e.stopPropagation();
                router.push(
                  `/${workspaceId}${Routes.CONTENT_SCANNER}/jobs?${new URLSearchParams({ search: report.brand?.name! }).toString()}`,
                );
              },
              isWarning: false,
            } satisfies ActionOption,
          ]
        : []),
      {
        icon: TrashIcon,
        label: 'Delete report',
        onClick: (e) => {
          e.stopPropagation();
          openModal();
        },
        isWarning: true,
      },
      {
        icon: XMarkIcon,
        label: 'Close report',
        onClick: (e) => {
          e.stopPropagation();
          onClose();
        },
        isWarning: false,
      },
    ],
    [onClose, openModal, report?.brand?.name],
  );

  const renderReviewButton = useCallback(() => {
    if (allDocumentsReviewed) {
      return (
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <DocumentCheckIcon className="size-4" strokeWidth={2} />
          <span>All Documents Reviewed</span>
        </div>
      );
    }

    if (isReviewed) {
      return (
        <Button
          size="sm"
          variant="outline"
          label="Reviewed"
          title="Mark as unreviewed"
          icon={<DocumentCheckIcon className="size-4" strokeWidth={2} />}
          isLoading={isMarkingReviewed}
          onClick={(e) => {
            e.stopPropagation();
            handleMarkAsReviewedChange(e, false);
          }}
        />
      );
    }

    return (
      <Button
        size="sm"
        variant="outline"
        label="Mark as reviewed"
        icon={<DocumentCheckIcon className="size-4" strokeWidth={2} />}
        isLoading={isMarkingReviewed}
        onClick={(e) => {
          e.stopPropagation();
          handleMarkAsReviewedChange(e, true);
        }}
      />
    );
  }, [allDocumentsReviewed, isReviewed, isMarkingReviewed, handleMarkAsReviewedChange]);

  return (
    <Overlay itemId={reportId} onClose={onClose}>
      <DrawerShell onClick={(e) => e.stopPropagation()}>
        <DrawerHeader
          title={
            <span className="flex min-w-0 items-center gap-2">
              <Link
                href={`/${workspaceId}${Routes.CONTENT_SCANNER}/brand-reports/${brandId}/all-reports`}
                className="text-foreground hover:underline"
              >
                {report?.brand?.name}
              </Link>
              {report?.name ? (
                <span className="text-muted-foreground font-normal">— {report.name}</span>
              ) : null}
            </span>
          }
          onClose={onClose}
          trailing={
            <>
              {renderReviewButton()}
              <SkeletonActionButton options={actionOptions} />
            </>
          }
        />
        <DrawerBody className="space-y-6">
          {isReportLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : (
            <>
              <RecentHighRiskIssues reportId={reportId} />
              <ScannedPages reportId={reportId} brandId={brandId} />
            </>
          )}
        </DrawerBody>
      </DrawerShell>
      <ConfirmationModal
        modalRef={modalRef}
        onClose={closeModal}
        onConfirm={handleDelete}
        header="Delete Report"
        message="This removes the report from scan history. You can undo from the toast that follows."
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </Overlay>
  );
}
